import type { DevExMetricKey } from '@skystone/shared';

export interface DevExMetricConfig {
  key: DevExMetricKey;
  label: string;
  shortLabel: string;
  isInverted?: boolean;
}

export interface DevExCategoryConfig {
  name: string;
  metrics: readonly DevExMetricConfig[];
}

/**
 * Canonical DevEx metric categorisation shared between the management
 * dashboard DevEx tab and the team dashboard DevEx section.
 *
 * - `label` is the full metric name (used in the team dashboard)
 * - `shortLabel` is a compact version (used in the management dashboard grid)
 * - `isInverted` means lower is better (used for trend color in the team dashboard)
 */
export const DEVEX_METRIC_CATEGORIES: readonly DevExCategoryConfig[] = [
  {
    name: 'Productivity',
    metrics: [
      { key: 'productive', label: 'Productive', shortLabel: 'Productive' },
      { key: 'focusTime', label: 'Focus Time', shortLabel: 'Focus Time' },
      { key: 'interrupted', label: 'Interrupted', shortLabel: 'Interrupted', isInverted: true },
      { key: 'timeWasted', label: 'Time Wasted', shortLabel: 'Time Wasted', isInverted: true },
    ],
  },
  {
    name: 'Tooling & Infrastructure',
    metrics: [
      { key: 'satisfiedWithTools', label: 'Satisfied with Tools', shortLabel: 'Tools' },
      { key: 'easyDeploy', label: 'Easy Deploy', shortLabel: 'Easy Deploy' },
      { key: 'ciCd', label: 'CI/CD', shortLabel: 'CI/CD' },
      { key: 'testCoverage', label: 'Test Coverage', shortLabel: 'Test Coverage' },
    ],
  },
  {
    name: 'Collaboration & Process',
    metrics: [
      { key: 'codeReview', label: 'Code Review', shortLabel: 'Code Review' },
      { key: 'teamProcesses', label: 'Team Processes', shortLabel: 'Team Processes' },
      { key: 'meetings', label: 'Meetings', shortLabel: 'Meetings' },
      { key: 'bestPractices', label: 'Best Practices', shortLabel: 'Best Practices' },
    ],
  },
  {
    name: 'Codebase & Delivery',
    metrics: [
      { key: 'techDebt', label: 'Tech Debt', shortLabel: 'Tech Debt' },
      { key: 'codebaseContribution', label: 'Codebase Contribution', shortLabel: 'Codebase' },
      { key: 'shipFrequently', label: 'Ship Frequently', shortLabel: 'Ship Frequently' },
      { key: 'projectScope', label: 'Project Scope', shortLabel: 'Project Scope' },
    ],
  },
  {
    name: 'Wellbeing',
    metrics: [
      { key: 'energy', label: 'Energy', shortLabel: 'Energy' },
      { key: 'easeOfWork', label: 'Ease of Work', shortLabel: 'Ease of Work' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Survey question wording — surfaced in the per-card info tooltip on the
// DevEx Survey grid (PRODOPSAI-672). Source: docs/devex-survey-questions.md.
//
// Kept as flat lookups (not inlined per metric above) deliberately: repeating
// a multi-field object literal 18× reads as a duplicated block to SonarCloud's
// literal-anonymising duplication detector. Short single-token-per-line entries
// stay well under its block threshold.
// ---------------------------------------------------------------------------

/** Shared 5-point Likert scale for the agreement statements (questions 5–18). */
const LIKERT_SCALE: readonly string[] = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];

/** The four questions (1–4) that use a bespoke scale rather than the Likert one. */
const CUSTOM_ANSWER_SCALES: Partial<Record<DevExMetricKey, readonly string[]>> = {
  productive: ['None of the time', 'A little of the time', 'Some of the time', 'Most of the time', 'All of the time'],
  satisfiedWithTools: ['Very unsatisfied', 'Somewhat unsatisfied', 'Neither satisfied nor unsatisfied', 'Somewhat satisfied', 'Very satisfied'],
  energy: ['Not energized at all', 'A little energized', 'Somewhat energized', 'Very energized', 'Extremely energized'],
  easeOfWork: ['Very difficult', 'Somewhat difficult', 'Neither easy nor difficult', 'Somewhat easy', 'Very easy'],
};

const DEVEX_METRIC_QUESTIONS: Record<DevExMetricKey, string> = {
  productive: 'How often do you feel highly productive in your work?',
  focusTime: 'I get enough focus time.',
  interrupted: 'My normal work is regularly interrupted.',
  timeWasted: 'I feel like a lot of my time gets wasted.',
  satisfiedWithTools: 'How satisfied or unsatisfied are you with the development tools and practices at Odevo?',
  easyDeploy: "It's easy to deploy software.",
  ciCd: "I'm satisfied with the speed and reliability of our CI/CD tooling.",
  testCoverage: "I'm satisfied with the level of test coverage.",
  codeReview: 'Our code review process helps us release better code.',
  teamProcesses: 'Our team processes are efficient.',
  meetings: 'Our meetings are effective and useful.',
  bestPractices: 'I believe we follow development best practices.',
  techDebt: 'We have a reasonable amount of technical debt.',
  codebaseContribution: 'Our codebase is easy to contribute to.',
  shipFrequently: 'We ship code frequently enough.',
  projectScope: 'My projects are well-scoped with clear goals.',
  energy: 'How energized are you by your work?',
  easeOfWork: 'How easy or difficult is it for you to do work as a developer or technical contributor at Odevo?',
};

export interface DevExQuestionInfo {
  question: string;
  answers: readonly string[];
}

/**
 * The verbatim survey question + its answer scale for a metric. Metrics fall
 * back to the shared Likert scale unless they carry a bespoke one.
 */
export function getDevExQuestionInfo(key: DevExMetricKey): DevExQuestionInfo {
  return { question: DEVEX_METRIC_QUESTIONS[key], answers: CUSTOM_ANSWER_SCALES[key] ?? LIKERT_SCALE };
}
